import React from 'react';
import { Controller } from 'react-hook-form';
import CustomSelector from '@shared/components/CustomSelector';
import MuiInputTextWithRemainingChars from '@shared/components/MuiInputTextWithRemainingChars';
import MuiMultiSelectionDropDown from '@shared/components/MuiMultiSelectionDropDown';
import { Input } from '@mui/material';
import { permissionOptions } from '../constants/resourcePermissionOptions';
import { categoryOptions } from '../constants/resourceCategoryOptions';
import RESOURCE_FIELD_NAMES from '../constants/resourceFieldNames';

/**
 * @typedef {object} ResourceInputFieldsProps
 * @property {*} [control]
 * @property {boolean} [displayTitles]
 * @property {(fieldName: string, index: number) => string} [fieldNameProvider]
 * @property {number} [index]
 */

/**
 * @param {ResourceInputFieldsProps} props
 */
const ResourceInputFields = props => {
  const { control, displayTitles = false, fieldNameProvider, index = 0 } = props;

  return (
    <div className='resource-form-fields'>
      <div className='input-container'>
        {displayTitles && (
          <div className='label-area'>
            <div className='label bold'>Category</div>
          </div>
        )}
        <div className='resource-input'>
          <Controller
            control={control}
            name={fieldNameProvider(RESOURCE_FIELD_NAMES.CATEGORY, index)}
            render={({ field, fieldState: { error } }) => {
              return (
                <CustomSelector
                  placeholder='Select Category'
                  testId='category-selector'
                  options={categoryOptions}
                  error={!!error}
                  value={field.value}
                  setValue={field.onChange}
                />
              );
            }}
          />
        </div>
      </div>

      <div className='input-container'>
        {displayTitles && (
          <div className='label-area'>
            <div className='label bold'>Title</div>
          </div>
        )}
        <div className='resource-input'>
          <Controller
            control={control}
            name={fieldNameProvider(RESOURCE_FIELD_NAMES.TITLE, index)}
            render={({ field, fieldState: { error } }) => (
              <MuiInputTextWithRemainingChars
                placeholder='Enter Title'
                error={!!error}
                multiline={false}
                value={field.value}
                onChange={field.onChange}
                hideRemainingChars={false}
                maxLength={50}
                disableUnderline
                rows={1}
              />
            )}
          />
        </div>
      </div>

      <div className='input-container'>
        {displayTitles && (
          <div className='label-area'>
            <div className='label bold'>Description</div>
          </div>
        )}
        <div className='resource-input'>
          <Controller
            control={control}
            name={fieldNameProvider(RESOURCE_FIELD_NAMES.DESCRIPTION, index)}
            render={({ field, fieldState: { error } }) => (
              <MuiInputTextWithRemainingChars
                placeholder='Enter Description'
                error={!!error}
                multiline={true}
                value={field.value}
                onChange={field.onChange}
                hideRemainingChars={false}
                maxLength={150}
                disableUnderline
                rows={5}
              />
            )}
          />
        </div>
      </div>

      <div className='input-container'>
        {displayTitles && (
          <div className='label-area'>
            <div className='label bold'>Link</div>
          </div>
        )}
        <div className='resource-input'>
          <Controller
            control={control}
            name={fieldNameProvider(RESOURCE_FIELD_NAMES.LINK, index)}
            render={({ field, fieldState: { error } }) => (
              <Input
                disableUnderline
                placeholder='Enter URL'
                onChange={field.onChange}
                value={field.value}
                multiline={false}
                error={!!error}
                rows={1}
              />
            )}
          />
        </div>
      </div>

      <div className='input-container permission'>
        {displayTitles && (
          <div className='label-area'>
            <div className='label bold'>Permissions</div>
          </div>
        )}
        <div className='resource-input'>
          <Controller
            control={control}
            name={fieldNameProvider(RESOURCE_FIELD_NAMES.PERMISSIONS, index)}
            render={({ field, fieldState: { error } }) => (
              <MuiMultiSelectionDropDown
                disableClearable
                inputLabel='Select Permission(s)'
                options={permissionOptions}
                setSelectedValue={field.onChange}
                value={field.value}
                selectedValues={field.value}
                error={!!error}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default ResourceInputFields;
